import type { GraphQLAuditItem, GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';

import { useAuditDecisionActions } from './useAuditDecisionActions';
import { useAuditDecisionHelpers } from './useAuditDecisionHelpers';
import { useAuditDecisionPanelState } from './useAuditDecisionPanelState';
import { useDecisionFormData } from './useDecisionFormData';
import { useDeleteDecisionDialog } from './useDeleteDecisionDialog';
import { useLabelSmartInference } from './useLabelSmartInference';
import { usePolicySmartInference } from './usePolicySmartInference';
import { useSkipAuditItem } from './useSkipAuditItem';

interface UseAuditDecisionPanelProps {
  selectedItem: GraphQLAuditItem | null;
  currentUserDecision?: GraphQLAuditDecision;
  onSave: (decision: GraphQLAuditDecision) => void;
  onClose: () => void;
  isSessionCompleted: boolean;
  getClassificationResults: (item: GraphQLAuditItem | null) => Array<{ name: string; severity: number }>;
}

/**
 * Facade hook that provides a clean API for the AuditDecisionPanel
 * Reduces cognitive load by organizing the 8 existing hooks into a single interface
 */
export function useAuditDecisionPanel({
  selectedItem,
  currentUserDecision,
  onSave,
  onClose,
  isSessionCompleted,
  getClassificationResults,
}: UseAuditDecisionPanelProps) {
  // Helper functions
  const helpers = useAuditDecisionHelpers();

  // Form data management
  const { formData, setFormData } = useDecisionFormData({
    selectedItem,
    currentUserDecision,
    getTranscription: helpers.getTranscription,
  });

  // Smart inference for label assessments
  useLabelSmartInference({ formData, setFormData });

  // Smart inference for policy assessments
  usePolicySmartInference({ formData, setFormData });

  // Actions (save, mark all correct)
  const actions = useAuditDecisionActions({
    selectedItem,
    currentUserDecision,
    onSave,
    onClose,
    isSessionCompleted,
    getClassificationResults,
  });

  // Delete functionality
  const deleteDialog = useDeleteDecisionDialog({
    selectedItem,
    currentUserDecision,
    onClose,
  });

  // Skip functionality
  const skip = useSkipAuditItem({
    selectedItem,
    onClose,
  });

  // Panel state
  const panelState = useAuditDecisionPanelState();

  return {
    // Form data
    formData,
    setFormData,

    // Helper functions
    ...helpers,

    // Actions
    ...actions,

    // Delete functionality
    ...deleteDialog,

    // Skip functionality
    ...skip,

    // Panel state
    ...panelState,
  };
}
